import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CountriesService } from '../countries.service';
import {
  createCountryDocument,
  createCountryDocumentFailure,
  createCountryDocumentSuccess,
  deleteCountryDocument,
  deleteCountryDocumentFailure,
  deleteCountryDocumentsBulk,
  deleteCountryDocumentsBulkFailure,
  deleteCountryDocumentsBulkSuccess,
  deleteCountryDocumentSuccess,
  deleteCountryMessage,
  deleteCountryMessageFailure,
  deleteCountryMessagesBulk,
  deleteCountryMessagesBulkFailure,
  deleteCountryMessagesBulkSuccess,
  deleteCountryMessageSuccess,
  downloadCountryHelpDocument,
  downloadCountryHelpDocumentSuccess,
  loadCountries,
  loadCountriesFailure,
  loadCountriesSuccess,
  loadCountry,
  loadCountryDocumentById,
  loadCountryDocumentByIdSuccess,
  loadCountryDocuments,
  loadCountryDocumentsFailure,
  loadCountryDocumentsSuccess,
  loadCountryFailure,
  loadCountryMessage,
  loadCountryMessageFailure,
  loadCountryMessages,
  loadCountryMessagesFailure,
  loadCountryMessagesSuccess,
  loadCountryMessageSuccess,
  loadCountrySuccess,
  loadPaymentDetails,
  loadPaymentDetailsFailure,
  loadPaymentDetailsSuccess,
  updateCountryDocumentFailure,
  updateCountryDocumentSuccess,
  updateCountryHelpDocumentWithNewFile,
} from './countries.actions';
import { forkJoin, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ICountryHelpDocumentRequest } from '../countries.models';
import { DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CountriesEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(CountriesService);
  private readonly toastService = inject(DsToastService);
  private readonly translateService = inject(TranslateService);

  public loadCountryMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountryMessages),
      switchMap(() => {
        return this.service.getCountryMessages().pipe(
          map((messages) =>
            loadCountryMessagesSuccess({
              messages,
            })
          ),
          catchError((err) => of(loadCountryMessagesFailure({ error: err })))
        );
      })
    )
  );

  public loadCountryMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountryMessage),
      switchMap((action) =>
        this.service.getCountryMessageById(action.id).pipe(
          map((countryMessage) =>
            loadCountryMessageSuccess({ countryMessage })
          ),
          catchError((error) => of(loadCountryMessageFailure({ error })))
        )
      )
    )
  );

  public loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountries),
      switchMap(() => {
        return this.service.getCountries().pipe(
          map((countries) =>
            loadCountriesSuccess({
              countries,
            })
          ),
          catchError((err) => of(loadCountriesFailure({ error: err })))
        );
      })
    )
  );

  public loadCountry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountry),
      switchMap((action) =>
        this.service.getCountryById(action.id).pipe(
          map((country) => loadCountrySuccess({ country })),
          catchError((error) => of(loadCountryFailure({ error })))
        )
      )
    )
  );

  public loadCountryDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountryDocuments),
      switchMap(() => {
        return this.service.getCountryHelpDocuments().pipe(
          map((documents) =>
            loadCountryDocumentsSuccess({
              documents,
            })
          ),
          catchError((err) => of(loadCountryDocumentsFailure({ error: err })))
        );
      })
    )
  );

  public loadCountryHelpDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCountryDocumentById),
      switchMap((action) =>
        this.service.getCountryHelpDocumentById(action.id).pipe(
          map((document) => loadCountryDocumentByIdSuccess({ document })),
          catchError((error) => of(loadCountryFailure({ error })))
        )
      )
    )
  );

  public deleteCountryMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCountryMessage),
      switchMap(({ id }) =>
        this.service.deleteCountryMessage(id).pipe(
          map(() => deleteCountryMessageSuccess()),
          catchError((error) => of(deleteCountryMessageFailure({ error })))
        )
      )
    )
  );

  public deleteCountryMessagesBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCountryMessagesBulk),
      switchMap(({ messageIds }) =>
        forkJoin(
          messageIds.map((id) => this.service.deleteCountryMessage(id))
        ).pipe(
          map(() => deleteCountryMessagesBulkSuccess({ messageIds })),
          catchError((error) => of(deleteCountryMessagesBulkFailure({ error })))
        )
      )
    )
  );

  public reloadCountryMessagesAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCountryMessageSuccess, deleteCountryMessagesBulkSuccess),
      map(() => loadCountryMessages())
    )
  );

  public countryMessageDeleteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteCountryMessageSuccess, deleteCountryMessagesBulkSuccess),
        tap(() =>
          this.toastService.pushToast({
            id: 'delete-country-message-success',
            tone: 'positive',
            duration: 3000,
            toastText: this.translateService.instant(
              'common.toasts.updateSuccess'
            ),
          })
        )
      ),
    { dispatch: false }
  );

  public countryMessageDeleteFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteCountryMessageFailure, deleteCountryMessagesBulkFailure),
        tap(() =>
          this.toastService.pushToast({
            id: 'delete-country-message-failure',
            tone: 'critical',
            duration: 3000,
            toastText: this.translateService.instant(
              'common.toasts.updateFailure'
            ),
          })
        )
      ),
    { dispatch: false }
  );

  public deleteHelpDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCountryDocument),
      switchMap((action) =>
        this.service.deleteCountryHelpDocument(action.id).pipe(
          map(() => deleteCountryDocumentSuccess()),
          catchError((error) => of(deleteCountryDocumentFailure({ error })))
        )
      )
    )
  );

  public deleteHelpDocumentsBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCountryDocumentsBulk),
      switchMap(({ documentIds }) =>
        forkJoin(
          documentIds.map((id) => this.service.deleteCountryHelpDocument(id))
        ).pipe(
          map(() => deleteCountryDocumentsBulkSuccess({ documentIds })),
          catchError((error) =>
            of(deleteCountryDocumentsBulkFailure({ error }))
          )
        )
      )
    )
  );

  public createCountryHelpDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCountryDocument),
      switchMap(({ countryDocument }) => {
        const { file } = countryDocument.file;
        return this.service.createCountryHelpDocument(countryDocument).pipe(
          switchMap((response) => {
            return this.service
              .uploadFile(file, response.file.preSignedUrl)
              .pipe(
                map(() => {
                  return createCountryDocumentSuccess();
                }),
                catchError((error) =>
                  of(createCountryDocumentFailure({ error }))
                )
              );
          }),
          catchError((error) => of(createCountryDocumentFailure({ error })))
        );
      })
    )
  );
  public updateCountryHelpDocumentWithNewFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCountryHelpDocumentWithNewFile),
      switchMap(({ countryDocument }) => {
        const { file } = countryDocument.file;
        return this.service
          .updateCountryHelpDocument(countryDocument.id, countryDocument)
          .pipe(
            switchMap((response) => {
              return this.service
                .uploadFile(file, response.file.preSignedUrl)
                .pipe(
                  map((response) => {
                    return updateCountryDocumentSuccess({
                      countryHelpDocument:
                        response as ICountryHelpDocumentRequest,
                    });
                  }),
                  catchError((error) =>
                    of(updateCountryDocumentFailure({ error }))
                  )
                );
            }),
            catchError((error) => of(updateCountryDocumentFailure({ error })))
          );
      })
    )
  );

  public loadPaymentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPaymentDetails),
      switchMap(() => {
        return this.service.getPaymentDetails().pipe(
          map((paymentDetails) =>
            loadPaymentDetailsSuccess({
              paymentDetails,
            })
          ),
          catchError((err) => of(loadPaymentDetailsFailure({ error: err })))
        );
      })
    )
  );

  public downloadHelpDocument = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadCountryHelpDocument),
      switchMap(({ id }) =>
        this.service.getCountryHelpDocumentById(id).pipe(
          map((response) =>
            downloadCountryHelpDocumentSuccess({
              countryHelpDocument: response,
            })
          )
        )
      )
    )
  );
}
